<div class="sb-schema-form">
  @if (hasVisibleField(looseFields)) {
  <div class="sb-schema-form__loose">
    @for (item of looseItems; track trackByKey($index, item)) {
      <ng-container *ngTemplateOutlet="itemTpl; context: { $implicit: item }"></ng-container>
    }
  </div>
  }

  @for (section of sectionViews; track trackByKey($index, section)) {
    @if (isSectionVisible(section)) {
    <app-sb-input-form-section
      [attr.data-section]="section.key"
      [title]="section.title"
      [description]="section.description">
      @for (item of section.items; track trackByKey($index, item)) {
        <ng-container *ngTemplateOutlet="itemTpl; context: { $implicit: item }"></ng-container>
      }
      @if (section.key === fieldMappingSection && mappedGroups.length) {
        <ng-container *ngTemplateOutlet="mappedTpl"></ng-container>
      }
    </app-sb-input-form-section>
    }
  }
</div>

<!-- One entry of a section: a field, or a run of segment fields / field-name settings as one fixed-row table. -->
<ng-template #itemTpl let-item>
@switch (item.kind) {
    @case ('segments') {
      @if (groupFields(item).length) {
      <div
        class="sb-schema-form__field sb-schema-form__group"
        [attr.data-group]="item.key">
        <app-sb-input-complex-multiple
          itemLabel=""
          [columns]="segmentColumns"
          [showRowActions]="false"
          [minItems]="0"
          [trailingEmptyRow]="false"
          [ngModel]="groupRows(item)"
          [ngModelOptions]="{ standalone: true }">
          <ng-template sbInputComplexRow let-row>
            @if (fieldByKey(row['key']); as field) {
              <div class="col-md-4 mb-3 sb-schema-form__cell-text">
                <span class="sb-schema-form__cell-label">Platform group</span>
                <span class="sb-schema-form__group-name">{{ field.label }}@if (isRequired(field)) {
<span class="sb-schema-form__required" aria-hidden="true">*</span>
}</span>
                @if (field.description) {
                <span class="sb-schema-form__group-description">{{ field.description }}</span>
                }
              </div>
              <div class="col-md-3 mb-3 sb-schema-form__cell-text">
                <span class="sb-schema-form__cell-label">Contact type</span>
                <span class="sb-schema-form__contact-type">{{ contactType(field) }}</span>
              </div>
              <div class="col-md mb-3" [attr.data-field]="field.key" (focusout)="onFieldBlur(field)">
                <ng-container
                  *ngTemplateOutlet="controlTpl; context: {
                    $implicit: field,
                    value: model[field.key],
                    error: errorFor(field.key),
                    items: itemsFor(field),
                    cell: true
                  }">
                </ng-container>
              </div>
            }
          </ng-template>
        </app-sb-input-complex-multiple>
      </div>
      }
    }

    @case ('mapsTo') {
      @if (groupFields(item).length) {
      <div
        class="sb-schema-form__field sb-schema-form__group"
        [attr.data-group]="item.key">
        <app-sb-input-complex-multiple
          itemLabel=""
          [columns]="mapsToColumns"
          [showRowActions]="false"
          [minItems]="0"
          [trailingEmptyRow]="false"
          [ngModel]="groupRows(item)"
          [ngModelOptions]="{ standalone: true }">
          <ng-template sbInputComplexRow let-row>
            @if (fieldByKey(row['key']); as field) {
              <div class="col-md-4 mb-3 sb-schema-form__cell-text">
                <span class="sb-schema-form__cell-label">Snöball field</span>
                <span class="sb-schema-form__maps-to">{{ field.mapsTo?.label }}</span>
              </div>
              <div class="col-md-3 mb-3 sb-schema-form__cell-text">
                <span class="sb-schema-form__cell-label">Direction</span>
                <span class="sb-schema-form__direction" [attr.data-direction]="field.mapsTo?.direction">
                  <span aria-hidden="true">{{ field.mapsTo?.direction === 'push' ? '→' : '←' }}</span>
                  {{ field.mapsTo?.direction === 'push' ? 'to platform' : 'from platform' }}
                </span>
              </div>
              <div class="col-md mb-3" [attr.data-field]="field.key" (focusout)="onFieldBlur(field)">
                <ng-container
                  *ngTemplateOutlet="controlTpl; context: {
                    $implicit: field,
                    value: model[field.key],
                    error: errorFor(field.key),
                    items: itemsFor(field)
                  }">
                </ng-container>
              </div>
            }
          </ng-template>
        </app-sb-input-complex-multiple>
      </div>
      }
    }

    @default {
      <ng-container *ngTemplateOutlet="fieldTpl; context: { $implicit: item.field }"></ng-container>
    }
  }

</ng-template>

<!-- The plugin's fixed field mapping, read-only: rows a setting edits are shown by that setting. -->
<ng-template #mappedTpl>
  <div class="sb-schema-form__field sb-schema-form__mapped" data-field-mapping>
    <div class="sb-schema-form__mapped-title">Mapped automatically</div>
    <button
      type="button"
      class="btn btn-link btn-sm px-0 sb-schema-form__mapped-toggle"
      [attr.aria-expanded]="mappedExpanded"
      [attr.aria-controls]="mappedId"
      (click)="toggleMapped()">
      <i class="fa me-1" [class]="mappedExpanded ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
      {{ mappedExpanded ? 'Hide' : 'Show' }} {{ mappedCount }} automatically mapped field{{ mappedCount === 1 ? '' : 's' }}
    </button>
    <div class="sb-schema-form__mapped-body" [id]="mappedId" [hidden]="!mappedExpanded">
      @if (mappedExpanded) {
        @for (group of mappedGroups; track group) {
        <div
          class="sb-schema-form__mapped-group"
          [attr.data-contact-type]="group.influencerType">
          <div class="sb-schema-form__mapped-heading">{{ group.title }}</div>
          <div class="table-responsive sb-schema-form__mapped-scroll">
            <table class="table table-sm mb-0 sb-schema-form__mapped-table">
              <thead>
                <tr>
                  <th scope="col">Snöball field</th>
                  <th scope="col">Platform field</th>
                  <th scope="col">Notes</th>
                </tr>
              </thead>
              <tbody>
                @for (row of group.rows; track row) {
                <tr>
                  <td class="sb-schema-form__mapped-target">{{ row.label || row.target }}</td>
                  <td class="sb-schema-form__mapped-source"><code>{{ row.source }}</code></td>
                  <td class="sb-schema-form__mapped-note">{{ row.note }}</td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        }
      }
    </div>
  </div>
</ng-template>

<!-- One top-level field: its wrapper tracks blur, rows and mappings render their own row bodies. -->
<ng-template #fieldTpl let-field>
  @if (isFieldVisible(field)) {
  <div
    class="sb-schema-form__field"
    [attr.data-field]="field.key"
    (focusout)="onFieldBlur(field)">
@switch (field.type) {
      @case ('note') {
      <div class="alert sb-schema-form__note" role="note">{{ field.text }}</div>
      }

      @case ('copy') {
      <app-sb-copy-field
        [label]="field.label || ''"
        [help]="field.help || ''"
        [description]="field.description || ''"
        [value]="model[field.key] || ''">
      </app-sb-copy-field>
      }

      @case ('rows') {
      <app-sb-input-complex-multiple
        itemLabel=""
        [label]="field.label || ''"
        [help]="field.help || ''"
        [description]="field.description || ''"
        [required]="isRowsRequired(field)"
        [columns]="rowsColumns(field)"
        [emptyItem]="emptyRow(field)"
        [minItems]="rowsMinItems(field)"
        [trailingEmptyRow]="false"
        [error]="errorFor(field.key)"
        [ngModel]="model[field.key]"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="setValue(field, $event)">
        <ng-template sbInputComplexRow let-row let-index="index" let-patch="patch">
          @for (sub of field.fields; track trackByKey($index, sub)) {
          <div
            class="col-md mb-3"
            [attr.data-field]="rowPath(field, index, sub)"
            (focusout)="markTouched(rowPath(field, index, sub))">
            <ng-container
              *ngTemplateOutlet="controlTpl; context: {
                $implicit: sub,
                value: row[sub.key],
                error: errorFor(rowPath(field, index, sub)),
                items: itemsFor(sub, row),
                parent: field,
                row: row,
                index: index,
                patch: patch
              }">
            </ng-container>
          </div>
          }
        </ng-template>
      </app-sb-input-complex-multiple>
      }

      @case ('mapping') {
      <app-sb-input-complex-multiple
        itemLabel=""
        [label]="field.label || ''"
        [help]="field.help || ''"
        [description]="field.description || ''"
        [required]="isRequired(field) || !!field.requiredKeys?.length"
        [columns]="mappingColumns"
        [emptyItem]="mappingEmptyRow"
        [minItems]="1"
        [maxItems]="mappingMaxItems(field)"
        [canRemoveItem]="mappingRowRemovable(field)"
        [trailingEmptyRow]="false"
        [error]="errorFor(field.key)"
        [ngModel]="model[field.key]"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="setValue(field, $event)">
        <ng-template
          sbInputComplexRow
          let-row
          let-index="index"
          let-update="update"
          let-patch="patch">
          <div class="col-md-5 mb-3">
            <app-sb-input-select
              label="Snöball field"
              [items]="mappingKeyItems(field, index)"
              [clearable]="false"
              [disabled]="isRequiredMappingRow(field, row)"
              [ngModel]="row['key']"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="patch({ key: $event })">
            </app-sb-input-select>
          </div>
          <div class="col-md mb-3">
            <app-sb-input-text
              label="Platform field"
              [required]="isRequiredMappingRow(field, row)"
              [ngModel]="row['value']"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="update('value', $event)">
            </app-sb-input-text>
          </div>
        </ng-template>
      </app-sb-input-complex-multiple>
      }

      @case ('conditions') {
      <div class="sb-schema-form__conditions">
        <app-sb-input-complex-multiple
          itemLabel="Filter"
          addLabel="Add filter"
          emptyText="No filters: every record is synced."
          [label]="field.label || ''"
          [help]="field.help || ''"
          [description]="field.description || ''"
          [required]="isRequired(field)"
          [columns]="conditionColumns"
          [emptyItem]="emptyRule(field)"
          [minItems]="0"
          [maxItems]="conditionMaxRules(field)"
          [trailingEmptyRow]="false"
          [error]="errorFor(field.key)"
          [ngModel]="conditionRules(field)"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="setConditionRules(field, $event)">
          <ng-template sbInputComplexRow let-row let-index="index" let-patch="patch">
            <div
              class="col-md-4 mb-3"
              [attr.data-field]="rulePath(field, index, 'field')"
              (focusout)="markTouched(rulePath(field, index, 'field'))">
              @if (field.fieldOptions?.length) {
              <app-sb-input-select
                label="Field"
                [items]="conditionFieldItems(field, row)"
                [addTag]="field.strictFields !== true"
                addTagText="Use field"
                [clearable]="false"
                [error]="errorFor(rulePath(field, index, 'field'))"
                [ngModel]="row['field']"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="setRuleValue(field, index, 'field', $event, patch)">
              </app-sb-input-select>
              } @else {
                <app-sb-input-text
                  label="Field"
                  [error]="errorFor(rulePath(field, index, 'field'))"
                  [ngModel]="row['field']"
                  [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="setRuleValue(field, index, 'field', $event, patch)">
                </app-sb-input-text>
              }
            </div>
            <div
              class="col-md-3 mb-3"
              [attr.data-field]="rulePath(field, index, 'operator')"
              (focusout)="markTouched(rulePath(field, index, 'operator'))">
              <app-sb-input-select
                label="Operator"
                [items]="conditionOperatorItems(field)"
                [clearable]="false"
                [error]="errorFor(rulePath(field, index, 'operator'))"
                [ngModel]="row['operator']"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="setRuleValue(field, index, 'operator', $event, patch)">
              </app-sb-input-select>
            </div>
            <div
              class="col-md mb-3"
              [attr.data-field]="rulePath(field, index, 'value')"
              (focusout)="markTouched(rulePath(field, index, 'value'))">
              @if (ruleNeedsValue(row)) {
              <app-sb-input-text
                label="Value"
                [error]="errorFor(rulePath(field, index, 'value'))"
                [ngModel]="row['value']"
                [ngModelOptions]="{ standalone: true }"
                (ngModelChange)="setRuleValue(field, index, 'value', $event, patch)">
              </app-sb-input-text>
              }
            </div>
          </ng-template>
        </app-sb-input-complex-multiple>
        @if (conditionRules(field).length >= 2) {
        <div
          class="sb-schema-form__match"
          [attr.data-field]="field.key + '.match'">
          <app-sb-input-select
            label="Match"
            [items]="conditionMatchItems"
            [clearable]="false"
            [ngModel]="conditionMatch(field)"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="setConditionMatch(field, $event)">
          </app-sb-input-select>
        </div>
        }
      </div>
      }

      @default {
        <ng-container
          *ngTemplateOutlet="controlTpl; context: {
            $implicit: field,
            value: model[field.key],
            error: errorFor(field.key),
            items: itemsFor(field)
          }">
        </ng-container>
      }
    }

  </div>
  }
</ng-template>

<!-- The control for one value, top-level or inside a row (then `parent` / `patch` are set). -->
<ng-template
  #controlTpl
  let-field
  let-value="value"
  let-error="error"
  let-items="items"
  let-parent="parent"
  let-row="row"
  let-index="index"
  let-patch="patch"
  let-cell="cell">
@switch (controlFor(field)) {
    @case ('secret') {
      <app-sb-input-password
        autocomplete="new-password"
        [label]="field.label || ''"
        [placeholder]="field.placeholder || ''"
        [help]="field.help || ''"
        [description]="field.description || ''"
        [required]="isRequired(field)"
        [error]="error"
        [ngModel]="value"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
      </app-sb-input-password>
      <!-- Under the input it is about, not above its label. -->
      @if (field.configured) {
      <div class="sb-field__description sb-schema-form__secret-hint">{{ configuredSecretHint }}</div>
      }
    }

    @case ('number') {
    <app-sb-input-number
      [label]="field.label || ''"
      [placeholder]="field.placeholder || ''"
      [help]="field.help || ''"
      [description]="field.description || ''"
      [required]="isRequired(field)"
      [min]="field.min ?? null"
      [max]="field.max ?? null"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-number>
    }

    @case ('boolean') {
    <app-sb-input-checkbox
      [label]="field.label || ''"
      [help]="field.help || ''"
      [description]="field.description || ''"
      [required]="isRequired(field)"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-checkbox>
    }

    @case ('radio') {
    <app-sb-input-radio
      [label]="field.label || ''"
      [help]="field.help || ''"
      [description]="field.description || ''"
      [options]="field.options || []"
      [required]="isRequired(field)"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-radio>
    }

    <!-- In the segment table the row names the group and carries its description: no floating label, the placeholder asks for a segment. -->
    @case ('select') {
    <app-sb-input-select
      [label]="cell ? '' : (field.label || '')"
      [ariaLabel]="cell ? (field.label || '') + ' segment' : ''"
      [placeholder]="cell ? segmentCellPlaceholder(field) : selectPlaceholder(field, parent, row)"
      [help]="field.help || ''"
      [description]="cell ? '' : (field.description || '')"
      [items]="items"
      [clearable]="!isRequired(field)"
      [required]="!cell && isRequired(field)"
      [disabled]="awaitsSibling(field, row)"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-select>
    }

    @case ('textarea') {
    <app-sb-input-textarea
      [class.sb-schema-form__json]="field.type === 'json'"
      [label]="field.label || ''"
      [placeholder]="field.placeholder || ''"
      [help]="field.help || ''"
      [description]="field.description || ''"
      [required]="isRequired(field)"
      [rows]="field.type === 'json' ? 5 : 3"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-textarea>
    }

    @default {
    <app-sb-input-text
      [label]="field.label || ''"
      [placeholder]="field.placeholder || ''"
      [help]="field.help || ''"
      [description]="field.description || ''"
      [required]="isRequired(field)"
      [error]="error"
      [ngModel]="value"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="onControlChange(field, $event, parent, row, index, patch)">
    </app-sb-input-text>
    }
  }

</ng-template>
